// Componentes de tracking de compras terceirizadas.
//
//   • TerceirizadosPopover    — botão 📦 inline na linha da Logística
//   • ComprasSidebar          — barra slim fixa no lado direito (3 ícones
//                                verticais + badges). Click expande o drawer.
//   • ComprasDrawer           — overlay do lado direito com a lista detalhada
//                                de um tipo: filtro de data + dimensões C×P
//                                por móvel + botão marcar todos.

import { useState, useRef, useEffect, useMemo } from 'react'
import {
  Package, Check, X, ExternalLink, ChevronRight, Calendar, FileDown,
} from 'lucide-react'
import { gerarPdfTerceirizado } from '../utils/pdfTerceirizados'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  TERCEIRIZADOS, terceirizadosDoPedido, terceirizadosPendentes,
  todosTerceirizadosEncomendados, toggleTerceirizado,
  pedidosPendentesPorTipo, labelTerceirizado,
  moveisQueUsam, temDimensao,
} from '../data/terceirizados'

function fmtData(d) {
  if (!d || d === '0000-00-00') return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM', { locale: ptBR }) } catch { return d }
}

function hojePlus(dias) {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

// ────────────────────────────────────────────────────────────────────────────
// Popover por linha — botão 📦 que abre checkboxes dos terceirizados
// ────────────────────────────────────────────────────────────────────────────
export function TerceirizadosPopover({ pedido, onChange }) {
  const tem = terceirizadosDoPedido(pedido)
  if (tem.length === 0) return null

  const [open, setOpen] = useState(false)
  const ref             = useRef(null)
  const tudoEncomendado = todosTerceirizadosEncomendados(pedido)
  const enc             = new Set(pedido.terceirizadosEncomendados || [])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function toggle(idTerc) {
    const novo = toggleTerceirizado(pedido, idTerc)
    onChange({ ...pedido, terceirizadosEncomendados: novo })
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        title={tudoEncomendado
          ? `Tudo encomendado (${tem.map(labelTerceirizado).join(', ')})`
          : `${tem.length - enc.size} terceirizado(s) pendente(s) de ${tem.length}`
        }
        className={`p-1.5 rounded-lg transition-colors
          ${tudoEncomendado
            ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
            : 'text-amber-600 bg-amber-50 hover:bg-amber-100'}`}
      >
        <Package size={13} fill={tudoEncomendado ? 'currentColor' : 'none'} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg p-2 w-56"
          onClick={e => e.stopPropagation()}
        >
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-2 py-1">
            Compras terceirizadas
          </p>
          {tem.map(idTerc => {
            const ativo = enc.has(idTerc)
            return (
              <button
                key={idTerc}
                onClick={() => toggle(idTerc)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs transition-colors
                  ${ativo
                    ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                    : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors
                  ${ativo
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white border-slate-300'}`}>
                  {ativo && <Check size={10} strokeWidth={3} />}
                </span>
                <span className="flex-1">{labelTerceirizado(idTerc)}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Sidebar slim fixa + drawer expansível — sempre visível, discreto
// ────────────────────────────────────────────────────────────────────────────

const FILTROS_DATA = [
  { id: 'd7',   label: '7d',   dias: 7   },
  { id: 'd14',  label: '14d',  dias: 14  },
  { id: 'd30',  label: '30d',  dias: 30  },
  { id: 'tudo', label: 'Tudo', dias: null },
]

// Botão compacto pro header da Logística — fica ao lado dos botões de
// "Entregues / PDF do dia / Filtrar". Mostra os 3 tipos com mini-badges e
// abre o drawer ao clicar em qualquer um.
export function ComprasTerceirizadasButton({ pedidos, onMarcar }) {
  const [aberto, setAberto] = useState(null)
  const [filtroDataId, setFiltroDataId] = useState('14d')

  const dataAte = useMemo(() => {
    const f = FILTROS_DATA.find(x => x.id === filtroDataId)
    return f?.dias ? hojePlus(f.dias) : null
  }, [filtroDataId])

  const porTipo = useMemo(
    () => pedidosPendentesPorTipo(pedidos, { dataAte }),
    [pedidos, dataAte]
  )

  const totalPendente = TERCEIRIZADOS.reduce((s, t) => s + porTipo[t.id].length, 0)

  return (
    <>
      {/* Container fica visualmente com a mesma altura dos outros btn-secondary
          do header. 3 mini-segmentos clicáveis lado a lado, com badge de contagem. */}
      <div
        className="inline-flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden h-[34px]"
        title={totalPendente === 0
          ? 'Tudo encomendado'
          : `${totalPendente} item(ns) pendente(s) de compra`}
      >
        <div className="px-2 h-full flex items-center bg-slate-900 text-amber-300 text-[10px] font-black uppercase tracking-wider">
          <Package size={11} className="inline mr-1" />
          Comprar
        </div>
        {TERCEIRIZADOS.map(terc => {
          const count = porTipo[terc.id].length
          const vazio = count === 0
          // Label super curto pro botão compacto
          const sigla = terc.id === 'pePalito'    ? 'PÉ'
                      : terc.id === 'baseMadeira' ? 'MAD'
                      : 'MET'
          return (
            <button
              key={terc.id}
              type="button"
              onClick={() => setAberto(aberto === terc.id ? null : terc.id)}
              title={`${terc.label}: ${count} pendente${count === 1 ? '' : 's'}`}
              className={`relative h-full px-2.5 text-[10px] font-bold uppercase tracking-wider border-l border-slate-100 transition-colors
                ${vazio
                  ? 'text-emerald-600 hover:bg-emerald-50'
                  : 'text-amber-700 hover:bg-amber-50'}
                ${aberto === terc.id ? 'bg-amber-50' : ''}
              `}
            >
              {sigla}
              {!vazio
                ? <span className="ml-1 inline-flex items-center justify-center bg-amber-500 text-white text-[9px] font-black rounded-full min-w-[14px] h-[14px] px-1">{count}</span>
                : <span className="ml-1 text-emerald-500">✓</span>}
            </button>
          )
        })}
      </div>

      {/* Drawer expansível — igual ao anterior */}
      {aberto && (
        <ComprasDrawer
          tipoId={aberto}
          pedidos={porTipo[aberto]}
          filtroDataId={filtroDataId}
          setFiltroDataId={setFiltroDataId}
          onClose={() => setAberto(null)}
          onMarcar={onMarcar}
        />
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Drawer da direita com a lista detalhada
// ────────────────────────────────────────────────────────────────────────────
function ComprasDrawer({ tipoId, pedidos, filtroDataId, setFiltroDataId, onClose, onMarcar }) {
  const navigate = useNavigate()
  const terc = TERCEIRIZADOS.find(t => t.id === tipoId)

  function marcarTodos() {
    if (pedidos.length === 0) return
    if (!window.confirm(`Marcar ${terc.label} como ENCOMENDADO em ${pedidos.length} pedidos?`)) return
    pedidos.forEach(p => onMarcar(p, tipoId))
  }

  return (
    <>
      {/* backdrop discreto — não bloqueia totalmente */}
      <div
        className="fixed inset-0 z-30 bg-black/10"
        onClick={onClose}
      />

      <div
        className="fixed top-0 bottom-0 right-0 z-40 w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col"
      >
        {/* Header */}
        <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Package size={15} className="text-amber-300" />
            <h2 className="font-bold text-sm">{terc.label}</h2>
            <span className="bg-amber-500 text-slate-900 text-[10px] font-black rounded-full px-2 py-0.5">
              {pedidos.length}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <X size={16} />
          </button>
        </div>

        {/* Filtro de data + Gerar PDF */}
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                <Calendar size={10} /> Prazo até
              </p>
              <div className="flex gap-1">
                {FILTROS_DATA.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFiltroDataId(f.id)}
                    className={`px-2.5 py-1 rounded text-[11px] font-bold transition-colors
                      ${filtroDataId === f.id
                        ? 'bg-slate-900 text-amber-300'
                        : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => {
                if (pedidos.length === 0) return
                const filtroLabel = FILTROS_DATA.find(f => f.id === filtroDataId)?.label || '—'
                gerarPdfTerceirizado(tipoId, pedidos, { filtroLabel })
              }}
              disabled={pedidos.length === 0}
              title="Gerar PDF da lista pra enviar ao fornecedor"
              className="bg-[#111] text-amber-300 hover:bg-slate-800 text-[11px] font-bold rounded px-3 py-1.5 flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileDown size={11} />
              Gerar PDF
            </button>
          </div>
        </div>

        {/* Lista de pedidos */}
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
          {pedidos.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              <Check size={28} className="mx-auto mb-2 text-emerald-300" />
              <p>Nada pendente nesse período</p>
            </div>
          ) : (
            pedidos.map(p => {
              const moveis = moveisQueUsam(p, tipoId)
              return (
                <div
                  key={p.id}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-amber-300 transition-colors"
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                    <button
                      onClick={() => navigate(`/pedidos/${p.id}`)}
                      className="font-mono text-[11px] font-bold text-amber-700 hover:underline"
                      title="Abrir pedido"
                    >
                      {p.numero}
                    </button>
                    <ExternalLink size={9} className="text-slate-300" />
                    <span className="text-[11px] text-slate-700 truncate flex-1">{p.cliente?.nome || '—'}</span>
                    {p.prazo && (
                      <span className="text-[10px] text-slate-400 font-mono shrink-0">{fmtData(p.prazo)}</span>
                    )}
                    <button
                      onClick={() => onMarcar(p, tipoId)}
                      title="Marcar como encomendado"
                      className="text-emerald-600 hover:bg-emerald-100 rounded p-1 shrink-0"
                    >
                      <Check size={12} strokeWidth={3} />
                    </button>
                  </div>
                  {/* Móveis (dimensão em destaque + descrição completa do produto) */}
                  <ul className="px-3 py-2 space-y-2">
                    {moveis.map((m, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <ChevronRight size={10} className="text-slate-300 mt-1.5 shrink-0" />
                        <div className="min-w-0 flex-1 space-y-1">
                          {/* Linha 1: badge da dimensão (se aplicável) */}
                          {temDimensao(tipoId) && m.comprimento && m.profundidade && (
                            <span
                              className={`inline-block font-mono font-bold text-[11px] px-1.5 py-0.5 rounded
                                ${m.dimFonte === 'texto'
                                  ? 'bg-amber-100 text-amber-900 border border-amber-200'
                                  : 'bg-emerald-100 text-emerald-900'}`}
                              title={m.dimFonte === 'texto' ? 'Dimensão extraída do texto do modelo' : 'Dimensão estruturada'}
                            >
                              {m.comprimento}×{m.profundidade} cm
                            </span>
                          )}
                          {temDimensao(tipoId) && (!m.comprimento || !m.profundidade) && (
                            <span className="inline-block bg-red-50 text-red-700 border border-red-200 font-bold text-[10px] px-1.5 py-0.5 rounded">
                              medida ?
                            </span>
                          )}
                          {/* Linha 2: descrição COMPLETA do produto (quebra livre, fonte maior) */}
                          <p className="text-[11px] text-slate-700 leading-snug whitespace-normal break-words">
                            {m.modelo}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })
          )}
        </div>

        {/* Footer com marcar todos */}
        {pedidos.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 bg-white shrink-0">
            <button
              onClick={marcarTodos}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
            >
              <Check size={13} strokeWidth={3} />
              Marcar todos os {pedidos.length} como encomendados
            </button>
          </div>
        )}
      </div>
    </>
  )
}
